import type { Column } from "@shared/types/data-table"
import type { PaymentLink } from "@shared/types/payment-links"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createStatusColumn,
  createDateColumn,
  createTruncatedColumn,
  createCustomColumn
} from "./column-factories"
import { AppStatusBadge } from "@shared/components/ui/status-badge/AppStatusBadge"
import { Copy, ExternalLink } from "lucide-react"
import { formatDate } from '@shared/utils/date'
import { toast } from 'sonner'

const CHECKOUT_URL = import.meta.env.VITE_CHECKOUT_URL || 'http://localhost:5173'

type BadgeColor = 'info' | 'success' | 'pending' | 'warning' | 'neutral' | 'error' | 'indigo'

const TYPE_COLORS: Record<string, BadgeColor> = {
  'products-subscriptions': 'info',
  'cause-service': 'success',
  'subscription': 'info',
  'fixed': 'pending',
  'donation': 'success',
  'one-time': 'warning',
}

const TYPE_LABELS: Record<string, string> = {
  'products-subscriptions': 'Products',
  'cause-service': 'Service',
}

function getPaymentLinkType(link: PaymentLink): string {
  if (link.linkType) return link.linkType
  try {
    const metadata = link.metadata ? JSON.parse(link.metadata) : {}
    return metadata.paymentFor || 'cause-service'
  } catch {
    return link.type || 'unknown'
  }
}

export const PAYMENT_LINK_COLUMNS: Record<string, Column<PaymentLink>> = {
  name: createTruncatedColumn<PaymentLink>(
    "name",
    "Name",
    200,
    (link) => link.name,
    "font-medium"
  ),
  status: createStatusColumn<PaymentLink>("payment-link"),
  amount: createCustomColumn<PaymentLink>(
    "amount",
    "Amount",
    120,
    (link: PaymentLink) => {
      if (!link.amount) return <div className="text-muted-foreground text-sm">—</div>
      const formattedAmount = (link.amount / 100).toFixed(2)
      return (
        <div className="text-right font-medium">
          {formattedAmount} {link.currency || 'EUR'}
        </div>
      )
    }
  ),
  type: createCustomColumn<PaymentLink>(
    "type",
    "Type",
    140,
    (link: PaymentLink) => {
      const paymentFor = getPaymentLinkType(link)

      return (
        <div className="capitalize">
          <AppStatusBadge
            variant="generic"
            text={TYPE_LABELS[paymentFor] || paymentFor}
            color={TYPE_COLORS[paymentFor] || 'neutral'}
          />
        </div>
      )
    }
  ),
  usage: createCustomColumn<PaymentLink>(
    "currentUses",
    "Usage",
    100,
    (link: PaymentLink) => {
      const current = link.currentUses || 0
      const max = link.maxUses

      if (!max) {
        return <div className="text-sm text-muted-foreground">Unlimited</div>
      }

      const isMaxed = current >= max
      return (
        <div className={`text-sm font-medium ${isMaxed ? 'text-destructive' : ''}`}>
          {current} / {max}
        </div>
      )
    }
  ),
  expiresAt: createCustomColumn<PaymentLink>(
    "expiresAt",
    "Expires At",
    150,
    (link: PaymentLink) => {
      if (!link.expiresAt) {
        return <div className="text-sm text-muted-foreground">N/A</div>
      }
      const date = new Date(link.expiresAt)
      return (
        <div className="text-sm font-medium">
          {formatDate(date)}
        </div>
      )
    }
  ),
  revenue: createCustomColumn<PaymentLink>(
    "totalRevenue",
    "Total Revenue",
    140,
    (link: PaymentLink) => {
      if (!link.totalRevenue) return <div className="text-muted-foreground text-sm">—</div>
      const formattedRevenue = (link.totalRevenue / 100).toFixed(2)
      return (
        <div className="text-right font-medium">
          {formattedRevenue} {link.currency || 'EUR'}
        </div>
      )
    }
  ),
  success: createCustomColumn<PaymentLink>(
    "successRate",
    "Success",
    100,
    (link: PaymentLink) => {
      const rate = link.successRate || 0
      return (
        <div className="text-right font-medium">
          {rate.toFixed(1)}%
        </div>
      )
    }
  ),
  url: createCustomColumn<PaymentLink>(
    "url",
    "Payment Link URL",
    300,
    (link: PaymentLink) => {
      if (!link.shortCode) {
        return <div className="text-muted-foreground text-sm">No short code</div>
      }
      const url = `${CHECKOUT_URL}/pay/${link.shortCode}`
      return (
        <div className="flex items-center gap-2">
          <div className="text-sm font-mono truncate max-w-[200px]" title={url}>
            {url}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(url).then(() => {
                toast.success('Link copied to clipboard')
              }).catch(() => {
                toast.error('Failed to copy link')
              })
            }}
            className="p-1 hover:bg-muted rounded"
            title="Copy URL"
          >
            <Copy className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              window.open(url, '_blank')
            }}
            className="p-1 hover:bg-muted rounded"
            title="Open in new tab"
          >
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )
    }
  ),
  lastUsed: createDateColumn<PaymentLink>("lastUsed", "Last used", 150),
  created: createDateColumn<PaymentLink>("created", "Created", 150),
  updated: createDateColumn<PaymentLink>("updated", "Last Updated", 150),
}

const DEFAULT_COLUMNS = ["name", "status", "url", "amount", "type", "usage", "expiresAt", "revenue", "created", "updated"] as const

export const PAYMENT_LINK_STATUS_CONFIG = {
  all: DEFAULT_COLUMNS,
  active: DEFAULT_COLUMNS,
  draft: DEFAULT_COLUMNS,
  inactive: DEFAULT_COLUMNS,
  blocked: DEFAULT_COLUMNS,
  expired: DEFAULT_COLUMNS,
} as const

export const getPaymentLinkColumns = (status: string = 'all'): Column<PaymentLink>[] => {
  const keys = PAYMENT_LINK_STATUS_CONFIG[status as keyof typeof PAYMENT_LINK_STATUS_CONFIG] || PAYMENT_LINK_STATUS_CONFIG.all
  return keys.map(key => PAYMENT_LINK_COLUMNS[key]).filter(Boolean)
}

export const getPaymentLinkColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys = new Set(PAYMENT_LINK_STATUS_CONFIG[status as keyof typeof PAYMENT_LINK_STATUS_CONFIG] || PAYMENT_LINK_STATUS_CONFIG.all)
  return Object.entries(PAYMENT_LINK_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key as typeof DEFAULT_COLUMNS[number]),
    required: key === "name" || key === "status",
    order: index
  }))
}
